import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import {
  CalendarClockIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleDashedIcon,
  LinkIcon,
  LockIcon,
  MailIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  focusLabelFor,
  interviewDates,
  type ScheduledInterview,
  stageLabelFor,
} from '@/lib/dashboard-interviews'
import { cn } from '@/lib/utils'
import { toKey } from '../data/homework-data'
import { weekdayHeaders } from '../data/types'
import { useInterviews } from '../providers/interviews-provider'
import { InterviewFormDialog } from './interview-form-dialog'

// Stable accent per interview so multiple companies stay distinguishable
const ACCENTS = [
  {
    solid: 'bg-task-design/15 text-task-design border-task-design/50',
    dot: 'bg-task-design',
  },
  {
    solid: 'bg-task-leetcode/15 text-task-leetcode border-task-leetcode/50',
    dot: 'bg-task-leetcode',
  },
  {
    solid: 'bg-task-video/15 text-task-video border-task-video/50',
    dot: 'bg-task-video',
  },
  {
    solid: 'bg-task-exercise/15 text-task-exercise border-task-exercise/50',
    dot: 'bg-task-exercise',
  },
  {
    solid: 'bg-task-article/15 text-task-article border-task-article/50',
    dot: 'bg-task-article',
  },
]

function accentFor(interviews: ScheduledInterview[], id: string) {
  const idx = interviews.findIndex((i) => i.id === id)
  return ACCENTS[Math.max(idx, 0) % ACCENTS.length]
}

function dateSummaryFor(interview: ScheduledInterview): string {
  if (interview.scheduling.kind === 'tbd') return 'Date TBD'
  const dates = interviewDates(interview)
  if (interview.scheduling.kind === 'locked') {
    const start = new Date(`${dates[0]}T00:00:00`)
    const end = new Date(`${dates[dates.length - 1]}T00:00:00`)
    return dates.length === 1
      ? format(start, 'EEE, MMM d')
      : `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`
  }
  return `${dates.length} ${dates.length === 1 ? 'day' : 'days'} offered`
}

// ---------------------------------------------------------------------------
// Month view: locked ranges are solid chips; availability is dashed chips.
// Clicking a chip opens that interview's details in the side list.
// ---------------------------------------------------------------------------

function InterviewsMonth({
  selectedId,
  onSelect,
}: {
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const { interviews } = useInterviews()
  const [month, setMonth] = useState(() => startOfMonth(new Date()))

  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 })
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 })
  const days: Date[] = []
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d)

  const byDay = useMemo(() => {
    const map = new Map<
      string,
      { interview: ScheduledInterview; isStart: boolean; isEnd: boolean }[]
    >()
    for (const interview of interviews) {
      const dates = interviewDates(interview)
      const set = new Set(dates)
      for (const key of dates) {
        const prev = toKey(addDays(new Date(`${key}T00:00:00`), -1))
        const next = toKey(addDays(new Date(`${key}T00:00:00`), 1))
        const list = map.get(key) ?? []
        list.push({
          interview,
          isStart: !set.has(prev),
          isEnd: !set.has(next),
        })
        map.set(key, list)
      }
    }
    return map
  }, [interviews])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{format(month, 'MMMM yyyy')}</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => setMonth((m) => addMonths(m, -1))}
            aria-label="Previous month"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonth(startOfMonth(new Date()))}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => setMonth((m) => addMonths(m, 1))}
            aria-label="Next month"
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border">
        <div className="grid grid-cols-7 border-b bg-muted/50">
          {weekdayHeaders().map((d) => (
            <div
              key={d}
              className="px-2 py-1.5 text-center text-xs font-medium tracking-wide text-muted-foreground uppercase"
            >
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day, i) => {
            const key = toKey(day)
            const entries = byDay.get(key) ?? []
            const inMonth = isSameMonth(day, month)
            const weekCol = i % 7

            return (
              <div
                key={key}
                className={cn(
                  'flex min-h-20 flex-col gap-1 border-b p-1.5',
                  weekCol !== 6 && 'border-r',
                  i >= days.length - 7 && 'border-b-0',
                  !inMonth && 'bg-muted/30',
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-5 items-center justify-center self-start rounded-full text-[11px] font-medium tabular-nums',
                    isToday(day)
                      ? 'bg-primary text-primary-foreground'
                      : inMonth
                        ? 'text-foreground'
                        : 'text-muted-foreground/60',
                  )}
                >
                  {format(day, 'd')}
                </span>

                {entries.map(({ interview, isStart, isEnd }) => {
                  const locked = interview.scheduling.kind === 'locked'
                  const accent = accentFor(interviews, interview.id)
                  const showLabel = isStart || weekCol === 0
                  return (
                    <Tooltip key={interview.id}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => onSelect(interview.id)}
                          className={cn(
                            'flex h-5 min-w-0 items-center gap-1 border px-1 text-[10px] leading-none font-medium',
                            accent.solid,
                            locked ? 'border-solid' : 'border-dashed',
                            !locked && 'opacity-80',
                            selectedId === interview.id &&
                              'ring-1 ring-primary',
                            isStart || weekCol === 0
                              ? 'rounded-l-md'
                              : '-ml-2 rounded-l-none border-l-0 pl-2',
                            isEnd || weekCol === 6
                              ? 'rounded-r-md'
                              : '-mr-2 rounded-r-none border-r-0 pr-2',
                          )}
                        >
                          {showLabel && (
                            <>
                              {locked ? (
                                <LockIcon
                                  className="size-2.5 shrink-0"
                                  aria-hidden="true"
                                />
                              ) : (
                                <CalendarClockIcon
                                  className="size-2.5 shrink-0"
                                  aria-hidden="true"
                                />
                              )}
                              <span className="truncate">
                                {interview.company} ·{' '}
                                {stageLabelFor(
                                  interview.stage,
                                  interview.stageLabel,
                                )}
                              </span>
                            </>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {interview.company} —{' '}
                        {stageLabelFor(interview.stage, interview.stageLabel)}
                        {locked ? ' (locked in)' : ' (possible day)'}
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span
            className="h-3 w-6 rounded-sm border border-solid border-foreground/40 bg-foreground/10"
            aria-hidden="true"
          />
          Locked in
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-3 w-6 rounded-sm border border-dashed border-foreground/40"
            aria-hidden="true"
          />
          Availability given
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Compact list: collapsed rows that expand into full details in place
// ---------------------------------------------------------------------------

function InterviewDetails({
  interview,
  onEdit,
}: {
  interview: ScheduledInterview
  onEdit: (interview: ScheduledInterview) => void
}) {
  const { removeInterview, isSaving } = useInterviews()
  const kind = interview.scheduling.kind
  const dates = interviewDates(interview)

  const scheduleDetail =
    kind === 'tbd'
      ? 'Confirmed, waiting on a date'
      : kind === 'locked'
        ? dateSummaryFor(interview)
        : dates
            .map((d) => format(new Date(`${d}T00:00:00`), 'EEE d'))
            .join(', ')

  return (
    <div className="flex flex-col gap-3 border-t p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={cn(
            kind === 'locked'
              ? 'border-task-exercise/50 text-task-exercise'
              : 'border-dashed text-muted-foreground',
          )}
        >
          {kind === 'locked' ? (
            <LockIcon aria-hidden="true" />
          ) : kind === 'availability' ? (
            <CalendarClockIcon aria-hidden="true" />
          ) : (
            <CircleDashedIcon aria-hidden="true" />
          )}
          {kind === 'locked'
            ? 'Locked in'
            : kind === 'availability'
              ? 'Availability out'
              : 'Date TBD'}
        </Badge>
        <span className="text-sm text-muted-foreground">{scheduleDetail}</span>
      </div>

      {interview.focuses.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {interview.focuses.map((f) => (
            <Badge key={f} variant="outline" className="text-muted-foreground">
              {focusLabelFor(f)}
            </Badge>
          ))}
        </div>
      )}

      {interview.notes && (
        <p className="rounded-lg bg-muted/50 p-2.5 text-sm text-muted-foreground text-pretty">
          {interview.notes}
        </p>
      )}

      {(interview.contacts.length > 0 || interview.links.length > 0) && (
        <div className="flex flex-col gap-1.5">
          {interview.contacts.map((c) => (
            <span
              key={c.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground"
            >
              <span className="font-medium text-foreground">
                {c.name}
                {c.role && ` · ${c.role}`}
              </span>
              {c.email && (
                <a
                  href={`mailto:${c.email}`}
                  className="flex items-center gap-1 hover:text-foreground hover:underline"
                >
                  <MailIcon className="size-3" aria-hidden="true" />
                  {c.email}
                </a>
              )}
              {c.phone && (
                <a
                  href={`tel:${c.phone}`}
                  className="flex items-center gap-1 hover:text-foreground hover:underline"
                >
                  <PhoneIcon className="size-3" aria-hidden="true" />
                  {c.phone}
                </a>
              )}
            </span>
          ))}
          {interview.links.map((l) => (
            <a
              key={l.id}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              <LinkIcon className="size-3" aria-hidden="true" />
              {l.label || l.url}
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(interview)}
          disabled={isSaving}
          aria-label={`Edit ${interview.company} interview`}
        >
          <PencilIcon />
          Edit
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void removeInterview(interview.id).catch(() => {
              // mutationError is surfaced by the provider; avoid unhandled rejection.
            })
          }}
          disabled={isSaving}
          aria-label={`Remove ${interview.company} interview`}
        >
          <Trash2Icon />
          Remove
        </Button>
      </div>
    </div>
  )
}

function CompactInterviewItem({
  interview,
  expanded,
  isPast,
  onToggle,
  onEdit,
}: {
  interview: ScheduledInterview
  expanded: boolean
  isPast?: boolean
  onToggle: () => void
  onEdit: (interview: ScheduledInterview) => void
}) {
  const { interviews } = useInterviews()
  const accent = accentFor(interviews, interview.id)

  return (
    <div
      className={cn(
        'rounded-lg border',
        expanded && 'ring-1 ring-primary/40',
        isPast && !expanded && 'opacity-60',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${interview.company} interview details`}
        className="flex w-full items-center gap-2.5 p-2.5 text-left"
      >
        <span
          className={cn('size-2.5 shrink-0 rounded-full', accent.dot)}
          aria-hidden="true"
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium">
              {interview.company}
            </span>
            <Badge variant="secondary" className="shrink-0">
              {stageLabelFor(interview.stage, interview.stageLabel)}
            </Badge>
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {dateSummaryFor(interview)}
            {interview.roleTitle?.trim() && ` · ${interview.roleTitle.trim()}`}
          </span>
        </span>
        <ChevronDownIcon
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>
      {expanded && <InterviewDetails interview={interview} onEdit={onEdit} />}
    </div>
  )
}

function InterviewGroup({
  title,
  icon: Icon,
  interviews,
  selectedId,
  isPast,
  onToggle,
  onEdit,
}: {
  title: string
  icon: typeof LockIcon
  interviews: ScheduledInterview[]
  selectedId: string | null
  isPast?: boolean
  onToggle: (id: string) => void
  onEdit: (interview: ScheduledInterview) => void
}) {
  if (interviews.length === 0) return null
  return (
    <div className="flex flex-col gap-2">
      <h3 className="flex items-center gap-1.5 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        <Icon className="size-3" aria-hidden="true" />
        {title}
      </h3>
      <div className="flex flex-col gap-2">
        {interviews.map((interview) => (
          <CompactInterviewItem
            key={interview.id}
            interview={interview}
            expanded={selectedId === interview.id}
            isPast={isPast}
            onToggle={() => onToggle(interview.id)}
            onEdit={onEdit}
          />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section shell: tiny collapsed row until something exists
// ---------------------------------------------------------------------------

export function InterviewsSection() {
  const {
    interviews,
    isCoachingClient,
    isLoading,
    isSaving,
    error,
    mutationError,
    addInterview,
    updateInterview,
  } = useInterviews()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ScheduledInterview | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const handleEdit = (interview: ScheduledInterview) => {
    setEditing(interview)
    setFormOpen(true)
  }

  const handleSave = async (interview: ScheduledInterview) => {
    if (editing) {
      await updateInterview(interview)
    } else {
      await addInterview(interview)
    }
    // Keep `editing` until the dialog closes so the form does not flash into
    // add mode while `onOpenChange(false)` is still pending.
  }

  const handleToggle = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id))
  }

  const todayKey = toKey(new Date())
  const isPast = (interview: ScheduledInterview) => {
    const dates = interviewDates(interview)
    return dates.length > 0 && dates[dates.length - 1] < todayKey
  }

  const upcoming = interviews.filter((i) => !isPast(i))
  const past = interviews
    .filter(isPast)
    .sort((a, b) => (interviewDates(a)[0] < interviewDates(b)[0] ? 1 : -1))
  const locked = upcoming
    .filter((i) => i.scheduling.kind === 'locked')
    .sort((a, b) => (interviewDates(a)[0] < interviewDates(b)[0] ? -1 : 1))
  const availability = upcoming.filter(
    (i) => i.scheduling.kind === 'availability',
  )
  const tbd = upcoming.filter((i) => i.scheduling.kind === 'tbd')

  const summaryParts = [
    `${locked.length} locked in`,
    `${availability.length} awaiting scheduling`,
    ...(tbd.length > 0 ? [`${tbd.length} date TBD`] : []),
    ...(past.length > 0 ? [`${past.length} past`] : []),
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <CalendarClockIcon className="size-4" aria-hidden="true" />
          Loading interviews...
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-destructive/40 px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm text-destructive">
          <CalendarClockIcon className="size-4" aria-hidden="true" />
          Could not load interviews
        </span>
      </div>
    )
  }

  // Interview data lives on the coaching client record, so accounts without
  // one can only look — the create/update/remove endpoints reject them.
  if (!isCoachingClient) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-dashed px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <CalendarClockIcon className="size-4" aria-hidden="true" />
          Interview tracking is available for coaching clients
        </span>
      </div>
    )
  }

  return (
    <>
      {interviews.length === 0 ? (
        <div className="flex flex-col gap-2">
          {mutationError && (
            <p className="rounded-lg border border-destructive/40 px-3 py-2 text-sm text-destructive">
              {mutationError.message}
            </p>
          )}
          <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed px-4 py-2.5">
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarClockIcon className="size-4" aria-hidden="true" />
              No interviews on the calendar yet
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={isSaving}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              <PlusIcon />
              Add interview
            </Button>
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-4">
            {mutationError && (
              <p className="rounded-lg border border-destructive/40 px-3 py-2 text-sm text-destructive">
                {mutationError.message}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-lg font-semibold tracking-tight">
                  Your Interviews
                </h2>
                <p className="text-sm text-muted-foreground">
                  {summaryParts.join(' · ')}
                </p>
              </div>
              <Button
                size="sm"
                disabled={isSaving}
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              >
                <PlusIcon />
                Add
              </Button>
            </div>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
              <div className="flex flex-col gap-4">
                <InterviewGroup
                  title="Up next"
                  icon={LockIcon}
                  interviews={locked}
                  selectedId={selectedId}
                  onToggle={handleToggle}
                  onEdit={handleEdit}
                />
                <InterviewGroup
                  title="Availability out"
                  icon={CalendarClockIcon}
                  interviews={availability}
                  selectedId={selectedId}
                  onToggle={handleToggle}
                  onEdit={handleEdit}
                />
                <InterviewGroup
                  title="Date TBD"
                  icon={CircleDashedIcon}
                  interviews={tbd}
                  selectedId={selectedId}
                  onToggle={handleToggle}
                  onEdit={handleEdit}
                />
                <InterviewGroup
                  title="Past"
                  icon={CalendarClockIcon}
                  interviews={past}
                  selectedId={selectedId}
                  isPast
                  onToggle={handleToggle}
                  onEdit={handleEdit}
                />
              </div>

              <InterviewsMonth
                selectedId={selectedId}
                onSelect={(id) => setSelectedId(id)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <InterviewFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditing(null)
        }}
        initial={editing}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </>
  )
}
